import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../auth/AuthContext';
import { Icons } from './CenecSidebar';

const ROLES = ['admin', 'engineer', 'viewer'];

const UserManagement = ({ onClose }) => {
  const { user, getUsers, updateUser, deleteUser, register } = useAuth();
  const [users, setUsers] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', email: '', password: '', org: 'CENEC', role: 'viewer' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.role === 'admin';

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const list = await getUsers();
      setUsers(list);
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [getUsers]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleEdit = (u) => {
    setEditId(u.id);
    setEditForm({ name: u.name, email: u.email, role: u.role, org: u.org, status: u.status });
  };

  const handleSave = async (id) => {
    await updateUser(id, editForm);
    setEditId(null);
    await refresh();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this user?')) {
      await deleteUser(id);
      await refresh();
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setError('');
    const result = await register(addForm);
    if (!result.success) {
      setError(result.error);
      return;
    }
    // Update role after registration if not default viewer
    if (addForm.role !== 'viewer') {
      // Refresh to get the new user list, then find the new user
      const allUsers = await getUsers();
      const newUser = allUsers.find((u) => u.email === addForm.email);
      if (newUser) {
        await updateUser(newUser.id, { role: addForm.role });
      }
    }
    setShowAdd(false);
    setAddForm({ name: '', email: '', password: '', org: 'CENEC', role: 'viewer' });
    await refresh();
  };

  return (
    <div className="user-mgmt-overlay">
      <div className="user-mgmt-panel">
        <div className="user-mgmt-header">
          <h2>{Icons.users} User Management</h2>
          <button className="btn-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {isAdmin && (
          <div className="user-mgmt-actions">
            <button className="btn-accent" onClick={() => setShowAdd(!showAdd)}>
              + Add User
            </button>
          </div>
        )}

        {showAdd && (
          <form className="user-add-form" onSubmit={handleAdd}>
            {error && <div className="auth-error">{error}</div>}
            <div className="form-row">
              <input placeholder="Full Name" required value={addForm.name}
                onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} />
              <input placeholder="Email" type="email" required value={addForm.email}
                onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} />
            </div>
            <div className="form-row">
              <input placeholder="Password" type="password" required value={addForm.password}
                onChange={(e) => setAddForm({ ...addForm, password: e.target.value })} />
              <input placeholder="Organisation" value={addForm.org}
                onChange={(e) => setAddForm({ ...addForm, org: e.target.value })} />
              <select value={addForm.role} onChange={(e) => setAddForm({ ...addForm, role: e.target.value })}>
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="form-row">
              <button type="submit" className="btn-accent">Create User</button>
              <button type="button" className="btn-ghost" onClick={() => setShowAdd(false)}>Cancel</button>
            </div>
          </form>
        )}

        {loading ? (
          <div style={{ padding: '24px', color: '#8a9a95' }}>Loading users...</div>
        ) : (
          <div className="user-table-wrap">
            <table className="user-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Org</th>
                  <th>Status</th>
                  {isAdmin && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className={u.id === user?.id ? 'current-user' : ''}>
                    <td>
                      <span className="user-avatar-sm">{u.avatar}</span>
                    </td>
                    {editId === u.id ? (
                      <>
                        <td><input className="edit-input" value={editForm.name}
                          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></td>
                        <td><input className="edit-input" value={editForm.email}
                          onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></td>
                        <td>
                          <select className="edit-input" value={editForm.role}
                            onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}>
                            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                          </select>
                        </td>
                        <td><input className="edit-input" value={editForm.org}
                          onChange={(e) => setEditForm({ ...editForm, org: e.target.value })} /></td>
                        <td>
                          <select className="edit-input" value={editForm.status}
                            onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </td>
                        <td className="action-cell">
                          <button className="btn-icon save" onClick={() => handleSave(u.id)} title="Save">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                          </button>
                          <button className="btn-icon" onClick={() => setEditId(null)} title="Cancel">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>{u.name}</td>
                        <td className="text-muted">{u.email}</td>
                        <td><span className={`role-badge role-${u.role}`}>{u.role}</span></td>
                        <td>{u.org}</td>
                        <td><span className={`status-badge status-${u.status}`}>{u.status}</span></td>
                        {isAdmin && (
                          <td className="action-cell">
                            <button className="btn-icon" onClick={() => handleEdit(u)} title="Edit">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                            </button>
                            {u.id !== user?.id && (
                              <button className="btn-icon danger" onClick={() => handleDelete(u.id)} title="Delete">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                              </button>
                            )}
                          </td>
                        )}
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
